'use client';

import { trackMetric } from '@/helper/clientTelemetry';
import { useReportWebVitals } from 'next/web-vitals';

/** Callback type follows the installed Next.js version — no parallel metric type. */
export type ReportWebVitalsCallback = Parameters<typeof useReportWebVitals>[0];

type WebVitalMetric = Parameters<ReportWebVitalsCallback>[0];

const WEB_VITAL_AZURE_NAMES = {
  LCP: 'RUM.WebVital.LCP',
  INP: 'RUM.WebVital.INP',
  CLS: 'RUM.WebVital.CLS',
  TTFB: 'RUM.WebVital.TTFB',
  FCP: 'RUM.WebVital.FCP',
} as const;

type AllowedVitalName = keyof typeof WEB_VITAL_AZURE_NAMES;

const ALLOWED_VITAL_NAMES = new Set<string>(Object.keys(WEB_VITAL_AZURE_NAMES));

const ALLOWED_RATINGS = new Set(['good', 'needs-improvement', 'poor']);

const ALLOWED_NAVIGATION_TYPES = new Set(['navigate', 'reload', 'back-forward', 'back-forward-cache', 'prerender']);

export type RumSurface = 'website' | 'order' | 'campaign' | 'auth' | 'other';

export type RumRouteGroup =
  | '/magazin/*'
  | '/vorteilswelt/*'
  | '/service/*'
  | '/bestellung/*'
  | '/anmeldung-grundversorgung/*'
  | '/formulare/*'
  | '/wirfuersklima/*'
  | '/login'
  | '/registrieren'
  | 'other';

type RouteGroupRule = {
  routeGroup: RumRouteGroup;
  surface: RumSurface;
  /** Exact pathname match when true; otherwise prefix match (`path === prefix || path.startsWith(prefix + '/')`). */
  exact?: boolean;
  prefix: string;
};

/** Internal/dev routes — never send RUM samples (not even as `other` / `dev`). */
const EXCLUDED_PATH_PREFIXES = ['/guide', '/design-system', '/poc', '/__poc'] as const;

/**
 * Fixed allowlist — unknown paths become `other`, never a new dimension value.
 * More specific prefixes must appear before broader ones.
 */
const ROUTE_GROUP_RULES: readonly RouteGroupRule[] = [
  { prefix: '/bestellung', routeGroup: '/bestellung/*', surface: 'order' },
  { prefix: '/anmeldung-grundversorgung', routeGroup: '/anmeldung-grundversorgung/*', surface: 'order' },
  { prefix: '/formulare', routeGroup: '/formulare/*', surface: 'order' },
  { prefix: '/wirfuersklima', routeGroup: '/wirfuersklima/*', surface: 'campaign' },
  { prefix: '/login', routeGroup: '/login', surface: 'auth', exact: true },
  { prefix: '/registrieren', routeGroup: '/registrieren', surface: 'auth', exact: true },
  { prefix: '/magazin', routeGroup: '/magazin/*', surface: 'website' },
  { prefix: '/vorteilswelt', routeGroup: '/vorteilswelt/*', surface: 'website' },
  { prefix: '/service', routeGroup: '/service/*', surface: 'website' },
];
/** Pathname captured once for the document load (not re-labeled on client navigations). */
let documentPathname: string | undefined;

export function resetWebVitalsDocumentPathnameForTests(): void {
  documentPathname = undefined;
}

function getDocumentPathname(): string {
  if (documentPathname !== undefined) return documentPathname;
  if (typeof window === 'undefined') return '/';
  try {
    documentPathname = window.location.pathname || '/';
  } catch {
    documentPathname = '/';
  }
  return documentPathname;
}

function matchesPrefix(pathname: string, prefix: string, exact?: boolean): boolean {
  if (exact) return pathname === prefix;
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function normalizePathname(pathname: string): string {
  return pathname.split('?')[0]?.split('#')[0] || '/';
}

/** True for guide / design-system / poc routes that must not emit RUM metrics. */
export function isExcludedRumPathname(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return EXCLUDED_PATH_PREFIXES.some((prefix) => matchesPrefix(path, prefix));
}

/** Strip query/hash by accepting pathname only; map to fixed allowlist values. */
export function resolveRumRouteContext(pathname: string): {
  route_group: RumRouteGroup;
  surface: RumSurface;
} {
  const path = normalizePathname(pathname);
  for (const rule of ROUTE_GROUP_RULES) {
    if (matchesPrefix(path, rule.prefix, rule.exact)) {
      return { route_group: rule.routeGroup, surface: rule.surface };
    }
  }
  return { route_group: 'other', surface: 'website' };
}

function isAllowedVitalName(name: string): name is AllowedVitalName {
  return ALLOWED_VITAL_NAMES.has(name);
}

function normalizeRating(rating: unknown): string {
  if (typeof rating === 'string' && ALLOWED_RATINGS.has(rating)) return rating;
  return 'unknown';
}

function normalizeNavigationType(navigationType: unknown): string {
  if (typeof navigationType === 'string' && ALLOWED_NAVIGATION_TYPES.has(navigationType)) {
    return navigationType;
  }
  return 'unknown';
}

function getAppVersion(): string {
  return process.env.NEXT_PUBLIC_APP_VERSION || 'unknown';
}

/**
 * Normalize a web-vital sample and send it via the existing Application Insights client path.
 * Never throws; ignores non-allowlisted metric names.
 */
export async function sendWebVital(metric: WebVitalMetric): Promise<void> {
  try {
    const name = metric?.name;
    if (typeof name !== 'string' || !isAllowedVitalName(name)) return;

    const value = metric.value;
    if (typeof value !== 'number' || !Number.isFinite(value)) return;

    const pathname = getDocumentPathname();
    if (isExcludedRumPathname(pathname)) return;

    const { route_group, surface } = resolveRumRouteContext(pathname);

    await trackMetric(WEB_VITAL_AZURE_NAMES[name], value, {
      metric_name: name,
      rating: normalizeRating(metric.rating),
      navigation_type: normalizeNavigationType(metric.navigationType),
      surface,
      route_group,
      app_version: getAppVersion(),
    });
  } catch {
    // never throw from telemetry
  }
}
